import {
  type SearchMatch,
  searchOrGroup,
  searchRankSql,
} from '../../../../lib/search-term.js';
import { buildOrdersWhere, type OrdersFilterParams } from './list.js';
import type { DbClient } from './types.js';

export interface SearchOrdersDbParams {
  companyId: string;
  match: SearchMatch;
  /** The IAM per-record scope the list applies, so search sees what it sees. */
  scope: NonNullable<OrdersFilterParams['scope']>;
  limit: number;
}

export interface OrderSearchRow {
  id: string;
  orderNumber: string;
  orderType: string;
  /** Shown beside the number, since a search can match on it alone. */
  shipmentReference: string | null;
  isArchived: boolean;
  /** The other party: the buyer on a sales order, the supplier otherwise. */
  counterpartyName: string | null;
  rank: number;
}

/** The other party's name, from its own ABOUT profile. */
const COUNTERPARTY_NAME_SQL = `(
  SELECT COALESCE(NULLIF(p.data->'display_name'->>'value', ''), NULLIF(p.data->'official_name'->>'value', ''))
    FROM companies_service_company_profiles p
   WHERE p.for_company_id = CASE WHEN orders.supplier_company_id = orders.for_company_id
                                 THEN orders.buyer_company_id ELSE orders.supplier_company_id END
     AND p.by_company_id = p.for_company_id
     AND p.profile_type = 'ABOUT'
   LIMIT 1
)`;

const RANK_SQL = `LEAST(
  ${searchRankSql('orders.order_number', ':searchExact', ':searchPrefix')},
  ${searchRankSql('orders.shipment_reference', ':searchExact', ':searchPrefix')}
)`;

/**
 * The best few orders for a site-wide search: order number or shipment
 * reference, an exact match first, then starts-with, then the rest.
 *
 * Access comes from `buildOrdersWhere`, the list's own clause, with no other
 * filter: the viewer's company and its IAM per-record scope. Archived orders
 * are included and flagged, since a pasted order number should find its order
 * wherever it lives. Plain LIKE within one company, like the list.
 */
export async function searchOrdersDb(
  db: DbClient,
  params: SearchOrdersDbParams,
): Promise<OrderSearchRow[]> {
  const { conditions, binds } = buildOrdersWhere({
    companyId: params.companyId,
    scope: params.scope,
  });

  const rows = await db.runSql<OrderSearchRow>(
    `SELECT orders.id,
            orders.order_number,
            orders.order_type,
            orders.shipment_reference,
            orders.is_archived,
            ${COUNTERPARTY_NAME_SQL} AS counterparty_name,
            ${RANK_SQL} AS rank
       FROM orders
      WHERE ${conditions.join(' AND ')}
        AND ${searchOrGroup(['orders.order_number', 'orders.shipment_reference'], ':searchContains')}
      ORDER BY rank, orders.order_number, orders.id
      LIMIT :searchLimit`,
    {
      ...binds,
      searchExact: params.match.exact,
      searchPrefix: params.match.prefix,
      searchContains: params.match.contains,
      searchLimit: params.limit,
    },
  );

  return rows.map(row => ({
    ...row,
    isArchived: Boolean(row.isArchived),
    rank: Number(row.rank),
  }));
}
